import type React from "react";
import {Box, Button, Chip, Paper, Stack, Typography} from "@mui/material";
import {Add} from "@mui/icons-material";
import ValueChainIcon from "~/icons/svg/ValueChainIcon.svg?react";
import LockIcon from '~/icons/svg/LockIcon.svg?react';
import VerifiedIcon from '~/icons/svg/VerifiedIcon.svg?react';
import {COLORS} from "~/style/colors-ts";
import type {ChainCreditEntitlement, ChainCreditRequest} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";
import {creditsUsed} from "~/pages/supplier-360/tabs/SupplierValueChain/chainCredits";
import {ChainPendingRequest} from "./ChainPendingRequest";
import {SummaryRows} from "./SummaryRows";

/** The design's marketing copy for what one credit buys — kept verbatim, per product. */
const INCLUDES = [
    'Upstream tiers 1–3 with role and input type',
    'Downstream buyers with share of output',
    'Event impact by category, refreshed monthly',
];

type ChainLockedPanelProps = {
    entitlement: ChainCreditEntitlement;
    pendingRequests: ChainCreditRequest[];
    supplierName: string;
    onUnlock: () => void;
    onRequest: () => void;
};

/** A ghost node for the preview's skeleton chain. */
function GhostNode({focus = false}: {focus?: boolean}) {
    return (
        <Stack
            gap={0.75}
            sx={{
                flex: focus ? 1 : 'unset',
                p: 1.25,
                borderRadius: 1,
                border: `1px solid ${focus ? COLORS.primary[400] : COLORS.grey[100]}`,
                bgcolor: focus ? COLORS.primary[100] : COLORS.white[900],
                height: 80,
            }}
        >
            <Box sx={{height: 12, width: '70%', borderRadius: 3, bgcolor: COLORS.grey[100]}}/>
            <Box sx={{height: 12, width: '45%', borderRadius: 3, bgcolor: COLORS.grey[150]}}/>
        </Stack>
    );
}

/**
 * Locked state of the Value chain tab (`ChainLocked` in the design): preview, ledger, unlock action.
 * "Request credits" only shows once the balance is empty and nothing is pending.
 */
export const ChainLockedPanel: React.FC<ChainLockedPanelProps> = ({
                                                                      entitlement,
                                                                      pendingRequests,
                                                                      supplierName,
                                                                      onUnlock,
                                                                      onRequest,
                                                                  }) => {
    const remaining = entitlement.credit_balance;
    const granted = entitlement.credits_granted;
    const used = creditsUsed(entitlement);
    const none = remaining === 0;
    const hasPending = pendingRequests.length > 0;

    return (
        <Stack direction={{xs: 'column', md: 'row'}}>
            <Paper
                variant="outlined"
                sx={{
                    flex: 2,
                    minHeight: 280,
                    position: 'relative',
                    borderColor: 'grey.100',
                    p: 3,
                    background: `linear-gradient(180deg, rgb(248, 247, 252), ${COLORS.white[900]})`,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                }}
            >
                <Stack direction="row" alignItems="center" gap={5} sx={{opacity: 0.6, flex: 1}}>
                    <Stack gap={1.5} sx={{flex: 1}}><GhostNode/><GhostNode/><GhostNode/></Stack>
                    <GhostNode focus/>
                    <Stack gap={1.5} sx={{flex: 1}}><GhostNode/><GhostNode/></Stack>
                </Stack>
                <Chip
                    icon={<LockIcon width={14} height={14}/>}
                    label="Chain not yet built for this company"
                    size="small"
                    sx={{position: 'absolute', bottom: 24, left: 24, minHeight: 29, fontSize: 12, bgcolor: `${COLORS.white[900]} !important`, border: `1px solid ${COLORS.grey[100]}`}}
                />
            </Paper>

            <Paper variant="outlined" sx={{width: 384, borderColor: 'grey.100', p: 2.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none'}}>
                <Stack gap={2}>
                    <Chip
                        icon={<ValueChainIcon width={14} height={14}/>}
                        label="1 COMPANY CREDIT"
                        size="small"
                        color="secondary"
                        sx={{alignSelf: 'flex-start', fontWeight: 600}}
                    />
                    <Typography variant="h6" fontWeight={600} fontSize={18}>
                        {none ? "You're out of value chain credits" : `Unlock the value chain for ${supplierName}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {none
                            ? `Every company you map consumes one credit. You've mapped ${used} of ${granted}, so ${supplierName} can't be resolved until more credits are added to your plan.`
                            : 'Mapping a chain resolves this company against external registries, filings and ownership data. After unlocking, this view stays open to your whole team.'}
                    </Typography>

                    <Stack gap={0.75}>
                        {INCLUDES.map(item => (
                            <Stack key={item} direction="row" alignItems="center" gap={1}>
                                <VerifiedIcon width={16} height={16} color={COLORS.secondary[500]}/>
                                <Typography variant="body2" color="text.secondary">{item}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                    <SummaryRows rows={[
                        {title: 'Companies mapped', value: `${used} of ${granted}`},
                        {
                            title: none ? 'Credits left' : 'This unlock costs',
                            value: none ? '0' : `1 credit · ${remaining - 1} left after`,
                            valueColor: none ? 'error' : undefined,
                        },
                    ]}/>

                    {pendingRequests.map(request => <ChainPendingRequest key={request.id} request={request}/>)}

                    {!none && (
                        <Button
                            variant="contained"
                            startIcon={<Box sx={{display: 'flex'}}><ValueChainIcon width={16} height={16}/></Box>}
                            onClick={onUnlock}
                            sx={{textTransform: 'none', alignSelf: 'flex-start'}}
                        >
                            {`Unlock ${supplierName} · 1 credit`}
                        </Button>
                    )}
                    {none && !hasPending && (
                        <Button
                            variant="contained"
                            startIcon={<Add/>}
                            onClick={onRequest}
                            sx={{textTransform: 'none', alignSelf: 'flex-start'}}
                        >
                            Request credits
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );
};
